import type {
  CalendarQuery,
  DisplayValueKind,
  MasterDataSet,
} from '@complan/contracts';
import type { InitiativeListState } from '@/shared/url/initiative-list-search-params';

/**
 * TanStack Query cache keys.
 *
 * The calendar key is the *filter signature*: the query object canonicalised so
 * that two views with the same filters, year and orientation share one cache
 * entry regardless of the order the URL happened to serialise them in. This is
 * what lets an orientation switch re-derive from cache instead of refetching when
 * only the layout changed (design.md, "State ownership"), and it is why the key is
 * built from the parsed query rather than from the raw query string.
 */
export const queryKeys = {
  me: () => ['me'] as const,
  /**
   * The calendar bars/count/legend key. Deliberately **excludes** orientation:
   * the server's `bars`, `count` and `legend` are identical across the month and
   * calendar-week orientations (the geometry is a pure client-side re-derivation
   * over the stored, unclipped dates), and even the Sales_Cycle orientation
   * re-uses the same bars — it only additionally needs the year's cycle
   * definitions, which are their own query. Keying without orientation is what
   * lets an orientation switch re-derive the layout from cache with no refetch
   * (R10.2; design.md, "State ownership").
   */
  calendar: (query: CalendarQuery) =>
    ['calendar', canonicalSignature(orientationInvariant(query))] as const,
  /** The active Sales_Cycle definitions of one planning year (R6.1, R7.2). */
  salesCycles: (planningYear: number) => ['sales-cycles', planningYear] as const,
  /**
   * Every Sales_Cycle definition across years, active and inactive, as the
   * Initiative list's label source.
   *
   * A separate key from {@link salesCycles} rather than a `planningYear` of
   * `null`: it is a different response (all years, inactive included), and
   * sharing the key would let the list's broader read satisfy a calendar query
   * that must see one year's active cycles only.
   */
  salesCycleLabels: () => ['sales-cycle-labels'] as const,
  /**
   * Every **active** Sales_Cycle definition across years, as the Initiative
   * intake form's Sales_Cycle picker source.
   *
   * A third key beside {@link salesCycles} and {@link salesCycleLabels} because
   * it is a third response: not one year's cycles (the calendar's grid needs
   * exactly one year), and not inactive ones either (R6.6 keeps a deactivated
   * cycle out of *new* assignment proposals, and this picker only ever proposes).
   * Sharing either key would let one screen's read populate another's cache with
   * the wrong population.
   */
  selectableSalesCycles: () => ['reference', 'sales-cycles', 'selectable'] as const,
  /**
   * One page of the Initiative list, keyed by the whole view state.
   *
   * The state is serialised in a fixed field order rather than passed as an
   * object, so two states that differ only in property order collide on one entry.
   * The page window is part of the key: paging is a different read, and keeping it
   * in the key is what makes stepping back to a visited page instant.
   */
  initiativeList: (state: InitiativeListState, planningYear: number, carried?: string) =>
    [
      'initiative-list',
      planningYear,
      // The dimensions the screen carries but renders no control for
      // (`complan-governance` R6.3). Part of the key because two Drill_Throughs
      // differing only in a carried dimension are two different result sets, and
      // without it the second would be served the first's cached page.
      carried ?? '',
      [...state.statuses].sort().join(','),
      [...state.platformIds].sort().join(','),
      state.search ?? '',
      state.sortDirection,
      state.limit,
      state.offset,
    ] as const,
  /**
   * The requester's own Saved_Views plus the shared ones (R11.10-R11.16). Not
   * keyed by filter: the list is per user, not per calendar state, so opening,
   * renaming or sharing a view invalidates this one key.
   */
  /**
   * The Saved_Views of one screen (`complan-governance` R6.10). Keyed by screen, or
   * two screens' menus would share an entry and each would show the other's views.
   */
  savedViews: (screen: string) => ['saved-views', screen] as const,
  /**
   * Every screen's Saved_View list, for invalidation after a write.
   *
   * A create, rename, share toggle or delete can affect any screen's list - sharing
   * publishes a view to every user on whatever screen it belongs to - so a write
   * invalidates the prefix rather than guessing which screens to refresh.
   */
  savedViewsAll: () => ['saved-views'] as const,
  /** One export job's status, polled while it runs (R18.4). */
  exportJob: (jobId: string) => ['export-job', jobId] as const,
  /**
   * The Master_Data_Administration surface (task 23.6, R16). Each set, the two
   * fixed value sets, the configurable validation rules, the role assignments and
   * the operations log are their own keys so a write to one invalidates only that
   * table. The master-data key carries `includeInactive` because the two views
   * (active-only and all) are distinct reads (R16.5).
   */
  masterData: (set: MasterDataSet, includeInactive: boolean) =>
    ['admin', 'master-data', set, includeInactive] as const,
  /**
   * The values one master data set offers as *new* selections (R16.5) - the
   * intake form's controlled-value source, `GET /api/master-data/:set/selectable`.
   *
   * A different key from {@link masterData} on purpose, not a third variant of
   * its `includeInactive` flag: it is a different endpoint, under a different
   * capability, returning a different subset (active *and* effective as of today).
   * Sharing a key would let an administration read populate the form's cache with
   * records the form must not offer.
   *
   * `referencedIds` is part of the key because it changes the response: an
   * already-referenced inactive value is retained for R5.9, so a form editing
   * record A and one editing record B legitimately see different lists. Sorted so
   * the key is order-independent.
   */
  selectableMasterData: (set: MasterDataSet, referencedIds: readonly string[] = []) =>
    ['reference', 'master-data', set, [...referencedIds].sort().join(',')] as const,
  /** Assignable Owner / Backup Owner accounts, `GET /api/users/selectable` (R4.10, R4.11). */
  selectableUsers: (referencedIds: readonly string[] = []) =>
    ['reference', 'users', [...referencedIds].sort().join(',')] as const,
  statusFinality: (kind: DisplayValueKind) => ['admin', 'status-finality', kind] as const,
  validationRules: () => ['admin', 'validation-rules'] as const,
  roleAssignments: () => ['admin', 'roles'] as const,
  operationsBackups: () => ['admin', 'operations', 'backups'] as const,
  dataBackupStatus: () => ['admin', 'backup', 'status'] as const,
} as const;

/**
 * A calendar query with its orientation normalised away, so two views that
 * differ only in orientation collide on one cache entry. The orientation is a
 * pure rendering choice over the same payload; folding it out of the key is the
 * mechanism behind "switching orientation does not refetch".
 */
function orientationInvariant(query: CalendarQuery): CalendarQuery {
  return { ...query, orientation: 'month' };
}

/**
 * A stable, order-independent signature of a calendar query. Arrays are sorted so
 * `[a, b]` and `[b, a]` collide; object keys are visited in a fixed order so key
 * insertion order never changes the signature.
 */
export function canonicalSignature(query: CalendarQuery): string {
  return JSON.stringify(canonicalise(query));
}

function canonicalise(value: unknown): unknown {
  if (Array.isArray(value)) {
    return [...value].map(canonicalise).sort(compareStable);
  }
  if (value !== null && typeof value === 'object') {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(([, v]) => v !== undefined)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0));
    return entries.map(([k, v]) => [k, canonicalise(v)]);
  }
  return value;
}

function compareStable(a: unknown, b: unknown): number {
  const sa = JSON.stringify(a);
  const sb = JSON.stringify(b);
  return sa < sb ? -1 : sa > sb ? 1 : 0;
}
